import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import React, { useEffect, useRef, useState } from 'react';
import { DISPLAYPLATFORM, LOCAL_STORAGE_KEY, MESSAGE_TYPE, PLATFORM, PLAYER } from '../../constant';
import { addDevice, getSubscriber, saveSubscriberLoginHistory, sendOTP } from '../../Api/api';
import { useNavigate } from 'react-router-dom';
import { utility } from '../../utility/utility';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box } from '@mui/material';
import { DialogButton } from '../homePage/components/dialogButton';
import { useDispatch } from 'react-redux';
import { setProfiles, setUser } from '../../redux/applicationSlice';
import Keypad from './keypad';

const LoginTvScreen = ({ mobileNumber, setShowVerifyOTP, showVerifyOTP, setMobileNumber, ottSetting }) => {

    const otpRef = useRef();
    const userRef = useRef();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { ref: inputGroupRef, focusKey: inputFocusKey } = useFocusable({
        focusKey: 'LOGIN_INPUT_GROUP',
    });

    const { ref: loginButtonRef, focused: isButtonFocused, onEnterPress: onLoginEnterPress, setFocus } = useFocusable({
        focusKey: 'LOGIN_BUTTON',
        onEnterPress: () => onLoginEnter(),
        onArrowPress: (direction) => {
            if (direction === 'up') {
                setFocus("KEYPAD");
                return false;
            }
            if (direction === 'left') {
                return false;
            }
            return true;
        },
    });

    const { ref: dialogRef, focusKey: dialogFocusKey } = useFocusable({
        focusKey: 'DIALOG',
        isFocusBoundary: true,
        trackChildren: true,
        autoRestoreFocus: true,
        preferredChildFocusKey: 'DIALOG_OK_BUTTON',
        saveLastFocusedChild: true
    });

    const [message, setMessage] = useState('');
    const [dialogMessage, setDialogMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const onLoginEnter = async () => {
        if (isLoading) return;
        if (!showVerifyOTP) {
            const isValidEmail = /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/.test(mobileNumber);
            const isValidMobile = /^\d{10}$/.test(mobileNumber);

            if ((ottSetting?.authenticationType == MESSAGE_TYPE.EMAIL && !isValidEmail) || (ottSetting?.authenticationType != MESSAGE_TYPE.EMAIL && !isValidMobile)) {
                setMessage(`Please enter valid ${ottSetting?.authenticationType == MESSAGE_TYPE.EMAIL ? 'email' : 'mobile number'}`);
                return;
            }

            setMessage('');
            const subscriber = await getSubscriber(mobileNumber);
            if (subscriber.success) {
                userRef.current = subscriber.data;
                setIsLoading(true);
                const otpRes = await sendOTP(subscriber.data);
                setIsLoading(false);
                if (otpRes.success) {
                    otpRef.current = otpRes.data.otp.toString();
                    setMobileNumber('');
                    setShowVerifyOTP(true);
                } else {
                    setMessage(otpRes.message);
                }
            } else {
                setMessage(subscriber.message);
            }
        } else {
            if (mobileNumber.length === 4) {
                if (otpRef.current === mobileNumber) {
                    setMessage('');
                    setIsLoading(true);
                    const addDeviceRes = await addDevice(userRef.current._id, `${userRef.current.Name}'s Device`);
                    setIsLoading(false);

                    if (!addDeviceRes.success) {
                        setDialogMessage(addDeviceRes.message);
                        setFocus(dialogFocusKey);
                        return;
                    }
                    await saveSubscriberLoginHistory(userRef.current._id, PLATFORM[DISPLAYPLATFORM.TV].Description);
                    utility.setValue(LOCAL_STORAGE_KEY.user, userRef.current);
                    dispatch(setUser(userRef.current));
                    dispatch(setProfiles(userRef.current?.Profiles ?? []));
                    navigate('/tv');
                } else {
                    setMessage('Please enter valid OTP');
                }
            } else {
                setMessage('Please enter all 4 digits');
            }
        }
    };

    const onBack = (e) => {
        const { keyCode } = e;
        if (
            keyCode === PLAYER.VK_BACK_SPACE ||
            keyCode === PLAYER.VK_BACK ||
            keyCode === PLAYER.BACK_LG ||
            keyCode === PLAYER.RETURN
        ) {
            e.preventDefault();
            if (showVerifyOTP) {
                setMobileNumber('');
                setShowVerifyOTP(false);
            } else {
                navigate(-1);
            }
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', onBack);
        return () => window.removeEventListener('keydown', onBack);
    }, [showVerifyOTP]);

    const closeDialog = () => {
        setDialogMessage('');
    };

    return (
        <>
            <FocusContext.Provider value={inputFocusKey}>
                <div className='loginInputOuterDiv' ref={inputGroupRef}>
                    {showVerifyOTP ? (
                        <ul className='otpOuterDiv'>
                            <li>{mobileNumber[0] ?? ''}</li>
                            <li>{mobileNumber[1] ?? ''}</li>
                            <li>{mobileNumber[2] ?? ''}</li>
                            <li>{mobileNumber[3] ?? ''}</li>
                        </ul>
                    ) : (
                        <input
                            disabled
                            type="text"
                            value={mobileNumber}
                            className='loginInput'
                            placeholder={ottSetting?.authenticationType == MESSAGE_TYPE.EMAIL ? 'Email' : 'Mobile Number'}
                        />
                    )}
                    {message?.length > 0 && <div className='text-danger p-2 text-center'>{message}</div>}
                    <Keypad setMobileNumber={setMobileNumber} showVerifyOTP={showVerifyOTP} ottSetting={ottSetting} />
                    <button
                        ref={loginButtonRef}
                        onClick={onLoginEnterPress}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            padding: '10px 40px',
                            fontSize: '1.2rem',
                            backgroundColor: isButtonFocused ? 'white' : 'transparent',
                            color: isButtonFocused ? 'black' : 'white',
                            border: '2px solid white',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            outline: 'none',
                        }}
                    >
                        {isLoading ? (
                            <i className="fa fa-spinner fa-spin fa-x" aria-hidden="true" />
                        ) : showVerifyOTP ? (
                            <i className="fa fa-check" aria-hidden="true" />
                        ) : (
                            <i className="fa fa-sign-in" aria-hidden="true" />
                        )}
                        <span>{showVerifyOTP ? 'Verify OTP' : 'Login'}</span>
                    </button>
                </div>
                {dialogMessage?.length > 0 &&
                    <Dialog
                        open={true}
                        onClose={closeDialog}
                        sx={{
                            '& .MuiDialog-paper': {
                                outline: 'none',
                            }
                        }}
                    >
                        <FocusContext.Provider value={dialogFocusKey}>
                            <Box ref={dialogRef}>
                                <DialogTitle>Delete Device</DialogTitle>
                                <DialogContent>
                                    {dialogMessage}
                                </DialogContent>
                                <DialogActions>
                                    <DialogButton
                                        onClick={closeDialog}
                                        color="primary"
                                        focusKey="dialog-cancel-button"
                                    >
                                        Cancel
                                    </DialogButton>
                                    <DialogButton
                                        onClick={() => {
                                            navigate('/removeDevice', { state: { user: userRef.current, fromLogin: true } });
                                        }}
                                        color="error"
                                        focusKey="dialog-delete-button"
                                    >
                                        Delete
                                    </DialogButton>
                                </DialogActions>
                            </Box>
                        </FocusContext.Provider>
                    </Dialog>}
            </FocusContext.Provider>
        </>
    );
};

export default LoginTvScreen;
